import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { userService } from '../services/api';

const AddressContext = createContext();

export const useAddress = () => {
  const context = useContext(AddressContext);
  if (!context) {
    throw new Error('useAddress must be used within an AddressProvider');
  }
  return context;
};

export const AddressProvider = ({ children }) => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to extract addresses from API response
  const extractAddressesFromResponse = (response) => {
    if (response.data?.user?.addresses) {
      return response.data.user.addresses;
    } else if (response.user?.addresses) {
      return response.user.addresses;
    } else if (response.data?.addresses) {
      return response.data.addresses;
    } else if (Array.isArray(response.addresses)) {
      return response.addresses;
    } else if (Array.isArray(response.data)) {
      return response.data;
    } else if (Array.isArray(response)) {
      return response;
    }
    return [];
  };

  // Helper function to transform API addresses to expected format
  const transformAddresses = (userAddresses) => {
    return userAddresses.map((addr, index) => ({
      id: addr._id || addr.id || `addr_${index}`,
      userId: user?.id,
      type: addr.type || 'home',
      title: addr.type === 'home' ? 'Home' : addr.type === 'work' ? 'Work' : addr.type === 'other' ? 'Other' : 'Home',
      name: addr.name || user?.name || '',
      address: addr.addressLine1 || addr.address || '',
      addressLine1: addr.addressLine1 || addr.address || '',
      addressLine2: addr.addressLine2 || '',
      city: addr.city || '',
      state: addr.state || '',
      postalCode: addr.postalCode || '',
      country: addr.country || 'Pakistan',
      phone: addr.phone || user?.phone || '',
      isDefault: addr.isDefault || false
    }));
  };

  // Load addresses from API when user changes
  useEffect(() => {
    const loadAddresses = async () => {
      if (!user) {
        setAddresses([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        console.log('📍 AddressContext: Loading addresses for user:', user.id);
        const response = await userService.getAddresses();
        
        console.log('📍 AddressContext: Full response from API:', response);
        
        const userAddresses = extractAddressesFromResponse(response);
        console.log('📍 AddressContext: Extracted addresses:', userAddresses);
        
        const transformedAddresses = transformAddresses(userAddresses);
        console.log('📍 AddressContext: Transformed addresses:', transformedAddresses);
        
        setAddresses(transformedAddresses);
      } catch (err) {
        console.error('❌ AddressContext: Error loading addresses:', err);
        setError(err.message || 'Failed to load addresses');
        setAddresses([]);
      } finally {
        setLoading(false);
      }
    };

    loadAddresses();
  }, [user]);

  const getUserAddresses = (userId) => {
    if (!userId) return addresses;
    return addresses.filter(addr => addr.userId === userId);
  };

  const getDefaultAddress = (userId) => {
    const userAddresses = getUserAddresses(userId);
    return userAddresses.find(addr => addr.isDefault) || userAddresses[0] || null;
  };

  const reloadAddresses = async () => {
    try {
      const response = await userService.getAddresses();
      const userAddresses = extractAddressesFromResponse(response);
      const transformedAddresses = transformAddresses(userAddresses);
      setAddresses(transformedAddresses);
      return true;
    } catch (err) {
      console.error('Error reloading addresses:', err);
      return false;
    }
  };

  const addAddress = async (addressData) => {
    if (!user) return false;

    try {
      const result = await userService.addAddress(addressData);
      if (result.success) {
        await reloadAddresses();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error adding address:', err);
      return false;
    }
  };

  const updateAddress = async (addressId, addressData) => {
    if (!user) return false;

    try {
      const result = await userService.updateAddress(addressId, addressData);
      if (result.success) {
        await reloadAddresses();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error updating address:', err);
      return false;
    }
  };

  const deleteAddress = async (addressId) => {
    if (!user) return false;

    try {
      const result = await userService.deleteAddress(addressId);
      if (result.success) {
        await reloadAddresses();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error deleting address:', err);
      return false;
    }
  };

  const setDefaultAddress = async (addressId) => {
    if (!user) return false;

    try {
      const result = await userService.setDefaultAddress(addressId);
      if (result.success) {
        await reloadAddresses();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error setting default address:', err);
      return false;
    }
  };

  const value = {
    addresses,
    loading,
    error,
    getUserAddresses,
    getDefaultAddress,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress
  };

  return (
    <AddressContext.Provider value={value}>
      {children}
    </AddressContext.Provider>
  );
}; 